import { tw } from "@careinfo/tailwind";
import React, { useEffect, useRef, useState } from "react";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

const CropImage = ({
  imagePreview,
  setImagePreview,
  setCropModal,
  onChange = (file: any) => {},
  productId = null,
}) => {
  const cropperRef = useRef<HTMLImageElement | null>(null);
  const cropperInstance = useRef<Cropper | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [scaleX, setScaleX] = useState(1);

  useEffect(() => {
    if (!imagePreview) return;

    const initCropper = () => {
      if (cropperInstance.current) {
        cropperInstance.current.destroy(); // Destroy previous instance
      }
      if (cropperRef.current) {
        cropperInstance.current = new Cropper(cropperRef.current, {
          responsive: true,
          modal: true,
          viewMode: 2,
          autoCropArea: 1,
          cropBoxResizable: true,
          guides: true,
          background: true,
          dragMode: "move",
          zoomable: true,
          crop() {
            setZoomLevel(cropperInstance.current?.getData().scaleX || 1);
            setRotation(cropperInstance.current?.getData().rotate || 0);
          },
        });
      }
    };

    setTimeout(initCropper, 0);

    return () => {
      cropperInstance.current?.destroy();
      cropperInstance.current = null;
    };
  }, [imagePreview]);

  const base64ToFile = (base64: string, filename: string) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    const uniqueId = crypto.randomUUID();
    const uniqueName = Date.now();
    const uniqueFilename = `${filename.split(".")[0]}_${uniqueName}.${filename
      .split(".")
      .pop()}`;

    const croppedFile = new File([u8arr], uniqueFilename, { type: mime });
    return { croppedFile, uniqueId };
  };

  const handleCrop = () => {
    if (cropperInstance.current) {
      const croppedCanvas = cropperInstance.current.getCroppedCanvas();
      if (croppedCanvas) {
        const croppedBase64 = croppedCanvas.toDataURL();
        const { croppedFile, uniqueId } = base64ToFile(
          croppedBase64,
          "cropped-image.png"
        );
        const reader = new FileReader();
        reader.onload = () => {
          setImagePreview(reader.result as string);
          setCropModal(false);
        };
        reader.readAsDataURL(croppedFile);

        onChange(croppedFile);
      }
    }
  };

  const handleRotate = (angle: number) => {
    if (cropperInstance.current) {
      cropperInstance.current.rotateTo(angle);
      setRotation(angle);
    }
  };
  const handleClickRotate = (angle: number) => {
    if (cropperInstance.current) {
      cropperInstance.current.rotate(angle);
      // setRotation((prev) => prev + angle);
    }
  };

  const handleZoom = (zoom: number) => {
    if (cropperInstance.current) {
      setZoomLevel(zoom);
      cropperInstance.current.zoomTo(zoom);
    }
  };

  const handleReset = () => {
    if (cropperInstance.current) {
      cropperInstance.current.reset();
      setZoomLevel(1);
      setRotation(0);
    }
  };

  const handleFlip = () => {
    if (cropperInstance.current) {
      const newScaleX = scaleX === 1 ? -1 : 1;
      setScaleX(newScaleX);
      cropperInstance.current.scaleX(newScaleX);
    }
  };

  return (
    <div className={tw`flex flex-col `}>
      <div
        className={tw`bg-white p-4 rounded-lg flex flex-col items-center  max-h-[500px] `}
      >
        <img
          ref={cropperRef}
          src={imagePreview || ""}
          alt="Crop Preview"
          className={tw`w-full max-h-[400px]`}
        />
      </div>

      <div
        className={tw`flex justify-center w-full  items-center gap-4 px-4 mt-`}
      >
        <label className={tw`text-gray-700`}>
          Zoom
          <input
            defaultValue={zoomLevel}
            min={1}
            max={3}
            type="range"
            className="ml-2"
            step={0.1}
            aria-labelledby="Zoom"
            onChange={(e) => handleZoom(Number(e.target.value))}
          />
        </label>
        <label className={tw`text-gray-700`}>
          Rotate
          <input
            type="range"
            min="-180"
            max="180"
            className="ml-2"
            step="1"
            value={rotation}
            onChange={(e) => handleRotate(parseFloat(e.target.value))}
          />
        </label>
      </div>

      <div className={tw`flex items-center gap-x-4 justify-center w-full mt-4`}>
        <button
          className={tw`bg-gray-500 text-white px-4 py-2 rounded-lg`}
          onClick={handleFlip}
        >
          Flip
        </button>
        <button
          className={tw`bg-gray-500 text-white px-4 py-2 rounded-lg`}
          onClick={() => handleClickRotate(45)}
        >
          Rotate
        </button>
        <button
          className={tw`bg-gray-500 text-white px-4 py-2 rounded-lg`}
          onClick={handleReset}
        >
          Reset
        </button>
        <button
          className={tw`bg-primary text-white px-4 py-2 rounded-lg`}
          onClick={handleCrop}
        >
          Crop
        </button>
      </div>
    </div>
  );
};

export default CropImage;
